import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { makeRequest } from "../../utils/request";

export type OrganizationServiceAccountToken = {
  id: string;
  serviceAccountId: string;
  name: string | null;
  /** Raw token. Populated only on the create response. */
  token?: string;
  createdByUserId: string;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

type State = {
  /** tokens keyed by service account id, then by token id */
  byAccount: Record<string, Record<string, OrganizationServiceAccountToken>>;
  isLoading: boolean;
  isLoadingCreate: boolean;
  isLoadingDelete: boolean;
};

const initialState: State = {
  byAccount: {},
  isLoading: false,
  isLoadingCreate: false,
  isLoadingDelete: false,
};

const list = createAsyncThunk("organizationServiceAccountTokens/list", async ({ serviceAccountId }: { serviceAccountId: string }) => {
  const { data } = await makeRequest<{ data: OrganizationServiceAccountToken[] }>(`/v1/organization-service-accounts/${serviceAccountId}/tokens`);
  return { serviceAccountId, tokens: data };
});

const create = createAsyncThunk("organizationServiceAccountTokens/create", async ({ serviceAccountId, name }: { serviceAccountId: string; name?: string }) => {
  const { data } = await makeRequest<{ data: OrganizationServiceAccountToken }>(`/v1/organization-service-accounts/${serviceAccountId}/tokens`, {
    method: "POST",
    body: name ? { name } : {},
  });
  return { serviceAccountId, token: data };
});

const remove = createAsyncThunk(
  "organizationServiceAccountTokens/remove",
  async ({ serviceAccountId, tokenId }: { serviceAccountId: string; tokenId: string }) => {
    await makeRequest(`/v1/organization-service-accounts/${serviceAccountId}/tokens/${tokenId}`, {
      method: "DELETE",
    });
    return { serviceAccountId, tokenId };
  },
);

const slice = createSlice({
  name: "organizationServiceAccountTokens",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(list.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(list.fulfilled, (state, action) => {
        state.isLoading = false;
        const map: Record<string, OrganizationServiceAccountToken> = {};
        for (const t of action.payload.tokens) {
          map[t.id] = t;
        }
        state.byAccount[action.payload.serviceAccountId] = map;
      })
      .addCase(list.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(create.pending, (state) => {
        state.isLoadingCreate = true;
      })
      .addCase(create.fulfilled, (state, action) => {
        state.isLoadingCreate = false;
        const bucket = state.byAccount[action.payload.serviceAccountId] ?? {};
        // Drop the raw token from the cached copy — it's only revealed once
        // via the action payload to the originating component.
        const { token: _raw, ...persisted } = action.payload.token;
        bucket[persisted.id] = persisted as OrganizationServiceAccountToken;
        state.byAccount[action.payload.serviceAccountId] = bucket;
      })
      .addCase(create.rejected, (state) => {
        state.isLoadingCreate = false;
      })
      .addCase(remove.pending, (state) => {
        state.isLoadingDelete = true;
      })
      .addCase(remove.fulfilled, (state, action) => {
        state.isLoadingDelete = false;
        const bucket = state.byAccount[action.payload.serviceAccountId];
        if (bucket) {
          delete bucket[action.payload.tokenId];
        }
      })
      .addCase(remove.rejected, (state) => {
        state.isLoadingDelete = false;
      });
  },
});

export const organizationServiceAccountTokensActions = {
  ...slice.actions,
  list,
  create,
  remove,
};
export const organizationServiceAccountTokensReducer = slice.reducer;
